import T from "@marginly/ui/components/typography";
import React from "react";

import { getTokenSymbol } from "@/app/util";
import Space from "@/components/common/space";
import { ActiveTokenImage } from "@/components/common/token/active-token-image";
import { useWeb3Context } from "@/contexts/web3";
import { useMarginlyApi } from "@/hooks/api";
import { formatNum } from "@/util/format";
import { formatCoin } from "@/util/format-coin";

import * as S from "./styled";

interface Props {
  baseSymbol?: string;
  quoteSymbol?: string;
  apr: number | undefined;
  poolAddress?: `0x${string}`;
  isBase?: boolean;
}

export function LendPoolInfo({
  baseSymbol,
  quoteSymbol,
  apr,
  poolAddress,
  isBase = false,
}: Props) {
  const { internalChainId } = useWeb3Context();

  const totals = useMarginlyApi({
    endpoint: "totals",
    enabled: Boolean(poolAddress && internalChainId),
    args: [internalChainId, poolAddress],
  });
  const ratioBase =
    ((totals.data?.baseDebt || 0) / (totals.data?.baseCollateral || 1)) * 100;
  const ratioQuote =
    ((totals.data?.quoteDebt || 0) / (totals.data?.quoteCollateral || 1)) * 100;
  const ration = isBase ? ratioBase : ratioQuote;

  return (
    <S.Wrapper>
      <S.PairTitle>
        <div className="coins">
          <ActiveTokenImage token={baseSymbol} isActive={isBase} />
          <ActiveTokenImage token={quoteSymbol} isActive={!isBase} />
        </div>
        <div className="info">
          <T body>
            {getTokenSymbol(baseSymbol)} / {getTokenSymbol(quoteSymbol)}
          </T>
          <T body>
            {formatNum(apr ?? 0, {
              significantDecimals: 2,
              postfix: "%",
            })}{" "}
            APY
          </T>
        </div>
      </S.PairTitle>

      <Space height={16} heightMobile={16} />

      <S.Lent>
        <div className="painted" style={{ width: `${ration}%` }} />
      </S.Lent>

      <Space height={14} heightMobile={14} />

      {isBase ? (
        <T caption secondary>
          {formatCoin(totals.data?.baseDebt)} out of{" "}
          {formatCoin(totals.data?.baseCollateral)} {getTokenSymbol(baseSymbol)}{" "}
          borrowed by {"Short Sellers"}
        </T>
      ) : (
        <T caption secondary>
          {formatCoin(totals.data?.quoteDebt)} out of{" "}
          {formatCoin(totals.data?.quoteCollateral)}{" "}
          {getTokenSymbol(quoteSymbol)} borrowed by {"Long Buyers"}
        </T>
      )}
    </S.Wrapper>
  );
}
